import { Box, type BoxProps } from '@doist/marketist'

import { useTranslation } from 'next-i18next'
import { getUserPlan } from 'services/get-user-plan'

import { AvailableInPlanStatusTag } from './StatusTags/AvailableInPlanStatusTag'
import { ExperimentalFeatureStatusTag } from './StatusTags/ExperimentalFeatureStatusTag'
import { TagList } from './TagList/TagList'

import type { Article } from '@doist/help-center-utils'
import type { User } from 'types/user'

export function canDisplayMetaInfo(article: Article) {
    return (
        article.plans.length > 0 ||
        article.platforms.length > 0 ||
        isExperimentalFeature(article) ||
        article.labels.find((l) => l.startsWith('role-'))
    )
}

export type MetaInfoProps = BoxProps & { user?: User; article: Article }
export function MetaInfo({ user, article, ...rest }: MetaInfoProps) {
    const { t } = useTranslation('helpcenter')
    const plan = getUserPlan(user)
    const availableForYourPlan = plan !== undefined && article.plans.includes(plan)

    // Filter out Team (for now)
    // Ensure plans are not duplicated (after renaming)
    const plans = [
        ...new Set(article.plans.filter((p) => p !== 'team').map(sanitizePlanLabel)),
    ].map((p) => t(`article.${p}`))

    // Required user roles
    const roles = article.labels
        .filter((label) => label.startsWith('role-'))
        .map((label) => label.slice(5))

    const isExperimental = isExperimentalFeature(article)

    return (
        <Box {...rest}>
            {(plans.length > 0 || isExperimental) && (
                <TagList title={t('article.availableFor')} labels={plans}>
                    {user && plans.length > 0 ? (
                        <AvailableInPlanStatusTag isAvailable={availableForYourPlan} />
                    ) : null}
                    {isExperimentalFeature(article) ? (
                        <ExperimentalFeatureStatusTag
                            isOn={Boolean(user) && user?.features.beta === 1}
                        />
                    ) : null}
                </TagList>
            )}

            {article.platforms.length > 0 && (
                <TagList
                    title={t('article.platforms')}
                    labels={article.platforms.map((p) => t('article.' + p))}
                />
            )}

            {roles.length > 0 ? (
                <TagList
                    title={t('article.teamRole')}
                    labels={roles.map((role) => t(`article.${role}`))}
                />
            ) : null}
        </Box>
    )
}

function isExperimentalFeature(article: Article) {
    return article.labels.includes('experimental')
}

// Rename old plan labels to their latest names
function sanitizePlanLabel(label: string) {
    return label.replace('premium', 'pro').replace('free', 'beginner')
}
